import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { ArrowLeft, ArrowsClockwise, Sun, Moon, CheckCircle, CalendarDots, Flag, Drop, Leaf, Scissors, Bug, Flask, Flame, Sparkle, Wind, Target, Clock, Trophy, CaretDown, CaretUp } from '@phosphor-icons/react';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { AskMoeButton } from '../components/AskMoeDrawer';
import { API_URL } from '../config/api';

const SEASON_LABELS = {
  early_spring: 'Early Spring', late_spring: 'Late Spring', summer: 'Summer',
  early_fall: 'Early Fall', late_fall: 'Late Fall', winter: 'Winter Dormancy', unknown: 'Current'
};
const TIMING_LABELS = { today: 'Today', this_week: 'This Week', next_2_weeks: 'Next 2 Weeks', this_month: 'This Month', upcoming: 'Upcoming' };
const TIMING_COLORS = {
  today: 'bg-red-50 text-red-600 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800',
  this_week: 'bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800',
  next_2_weeks: 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800',
  this_month: 'bg-primary/5 text-primary border-primary/20',
  upcoming: 'bg-muted text-muted-foreground border-border',
};
const CATEGORY_COLORS = {
  mowing: 'text-green-600', irrigation: 'text-blue-500', fertilization: 'text-amber-600', weed_control: 'text-red-500',
  pest_control: 'text-orange-500', disease: 'text-purple-500', pgr: 'text-emerald-600', general: 'text-primary',
  aeration: 'text-yellow-700', overseeding: 'text-lime-600', soil: 'text-amber-800',
};
const ICON_MAP = { mowing: Scissors, irrigation: Drop, fertilization: Flask, weed_control: Bug, pest_control: Bug, disease: Flame, pgr: Leaf, general: Flag, aeration: Wind, overseeding: Sparkle, soil: Target };

export default function MyPlanPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [completedActions, setCompletedActions] = useState([]);
  const [actionTimestamps, setActionTimestamps] = useState({});
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [activity, setActivity] = useState([]);
  const [showActivity, setShowActivity] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [lawnSqft, setLawnSqft] = useState(0);

  const fetchCachedPlan = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/smart-plan/cached`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPlan(data);
        setCompletedActions(data.completed_actions || []);
        setActionTimestamps(data.action_timestamps || {});
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/smart-plan/history`, { credentials: 'include' });
      if (res.ok) setHistory(await res.json());
    } catch {}
  };

  const fetchActivity = async () => {
    try {
      const res = await fetch(`${API_URL}/api/smart-plan/activity`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setActivity(data.activities || []);
        setRetentionDays(data.retention_days);
      }
    } catch {}
  };

  const generatePlan = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/smart-plan`, { credentials: 'include' });
      if (res.status === 403) {
        const err = await res.json();
        toast.error(err.detail);
        setRefreshing(false);
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setPlan(data);
        setCompletedActions([]);
        toast.success('New plan generated');
        fetchHistory();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate plan');
      }
    } catch {
      toast.error('Could not reach the plan engine');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCachedPlan();
    fetchHistory();
    fetchActivity();
    // Fetch total lawn sqft so Ask Moe can size product amounts
    fetch(`${API_URL}/api/property/zones`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((zones) => {
        const total = (zones || []).reduce((sum, z) => sum + (parseFloat(z.sqft) || 0), 0);
        setLawnSqft(total);
      })
      .catch(() => {});
  }, [fetchCachedPlan]);

  const toggleComplete = async (idx) => {
    const isComplete = completedActions.includes(idx);
    const newCompleted = isComplete
      ? completedActions.filter(i => i !== idx)
      : [...completedActions, idx];
    setCompletedActions(newCompleted);

    // Persist to DB with timestamp
    try {
      const res = await fetch(`${API_URL}/api/smart-plan/complete-action`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action_idx: idx, completed: !isComplete }),
      });
      if (res.ok) {
        const data = await res.json();
        setActionTimestamps(data.action_timestamps || {});
        fetchActivity();
      }
    } catch {}

    // Check if all done
    if (!isComplete && plan?.actions && newCompleted.length === plan.actions.length) {
      toast.success('All actions complete! Generate your next plan when ready.', { duration: 5000 });
    }
  };

  const hasActions = plan && plan.actions && plan.actions.length > 0;
  const allComplete = hasActions && completedActions.length === plan.actions.length;
  const progressPct = hasActions ? Math.round((completedActions.length / plan.actions.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="my-plan-page">
      {/* Header */}
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/app')} data-testid="plan-back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <TurfPulseLogoSVG animated size={28} />
              <span className="font-semibold text-base tracking-tight hidden sm:inline">My Plan</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={generatePlan}
              disabled={refreshing}
              className="gap-1.5 text-xs"
              data-testid="plan-refresh-btn"
            >
              <ArrowsClockwise size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Generating...' : allComplete ? 'Next Plan' : 'Refresh Plan'}
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        )}

        {/* No plan yet */}
        {!loading && !hasActions && (
          <div className="text-center py-16" data-testid="plan-empty">
            <CalendarDots size={48} className="mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Your Smart Lawn Plan</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Generate a personalized action plan based on your GDD data, weather forecast, recent activities, and seasonal rules.
            </p>
            <Button onClick={generatePlan} disabled={refreshing} data-testid="plan-generate-btn">
              <ArrowsClockwise size={16} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Generating...' : 'Generate My Plan'}
            </Button>
          </div>
        )}

        {/* Plan content */}
        {!loading && hasActions && (
          <>
            {/* All Complete Celebration */}
            {allComplete && (
              <div className="mb-6 rounded-xl bg-primary/10 border border-primary/30 p-6 text-center" data-testid="plan-complete-banner">
                <Trophy size={40} weight="fill" className="mx-auto text-primary mb-2" />
                <h2 className="text-lg font-bold text-foreground mb-1">Plan Complete!</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  You knocked out every action. Your lawn is on track.
                </p>
                <Button onClick={generatePlan} disabled={refreshing} data-testid="plan-next-btn">
                  <ArrowsClockwise size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Generating...' : 'Generate Next Plan'}
                </Button>
              </div>
            )}

            {/* Summary header */}
            <div className="mb-8" data-testid="plan-summary">
              <div className="flex items-center gap-2 mb-2">
                {plan.season_phase && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full">
                    {SEASON_LABELS[plan.season_phase] || plan.season_phase}
                  </span>
                )}
                {plan.generated_at && (
                  <span className="text-[10px] text-muted-foreground">
                    Updated {new Date(plan.generated_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <p className="text-base sm:text-lg text-foreground leading-relaxed">{plan.summary}</p>
            </div>

            {/* Progress */}
            <div className="mb-6 flex items-center gap-3" data-testid="plan-progress">
              <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${allComplete ? 'bg-green-500' : 'bg-primary'}`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className={`text-xs shrink-0 font-medium ${allComplete ? 'text-green-600' : 'text-muted-foreground'}`}>
                {completedActions.length}/{plan.actions.length} done
              </span>
            </div>

            {/* Action items */}
            <div className="space-y-3" data-testid="plan-actions">
              {plan.actions.map((action, idx) => {
                const Icon = ICON_MAP[action.icon] || ICON_MAP[action.category] || Flag;
                const isComplete = completedActions.includes(idx);
                const timingColor = TIMING_COLORS[action.timing] || TIMING_COLORS.this_week;
                const catColor = CATEGORY_COLORS[action.category] || CATEGORY_COLORS.general;

                return (
                  <Card
                    key={idx}
                    className={`border transition-all duration-200 ${isComplete ? 'opacity-50 border-border/30' : 'border-border/50 hover:border-primary/30 hover:shadow-sm'}`}
                    data-testid={`plan-action-${idx}`}
                  >
                    <CardContent className="p-4 flex items-start gap-3">
                      <button
                        onClick={() => toggleComplete(idx)}
                        className={`mt-0.5 shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
                          ${isComplete ? 'bg-primary border-primary' : 'border-border hover:border-primary/50'}`}
                        data-testid={`plan-check-${idx}`}
                      >
                        {isComplete && <CheckCircle size={14} weight="fill" className="text-primary-foreground" />}
                      </button>
                      <div className={`shrink-0 w-9 h-9 rounded-lg bg-muted flex items-center justify-center ${catColor}`}>
                        <Icon size={18} weight="bold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className={`text-sm font-semibold text-foreground ${isComplete ? 'line-through' : ''}`}>
                            {action.action}
                          </h3>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 border ${timingColor}`}>
                            {TIMING_LABELS[action.timing] || action.timing}
                          </span>
                        </div>
                        <p className={`text-xs text-muted-foreground mt-1 leading-relaxed ${isComplete ? 'line-through' : ''}`}>
                          {action.detail}
                        </p>
                        {!isComplete && (
                          <div className="mt-2">
                            <AskMoeButton actionKey={action.category} lawnSqft={lawnSqft} />
                          </div>
                        )}
                        {isComplete && actionTimestamps[String(idx)] && (
                          <p className="text-[10px] text-green-600 mt-1 flex items-center gap-1">
                            <CheckCircle size={10} weight="fill" />
                            Completed {new Date(actionTimestamps[String(idx)]).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Plan History */}
            {history.length > 0 && (
              <div className="mt-10">
                <button
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => { setShowHistory(!showHistory); }}
                  data-testid="plan-history-toggle"
                >
                  <Clock size={16} />
                  Plan History ({history.length})
                  {showHistory ? <CaretUp size={14} /> : <CaretDown size={14} />}
                </button>
                {showHistory && (
                  <div className="mt-3 space-y-2" data-testid="plan-history-list">
                    {history.map((h, i) => {
                      const totalActions = h.plan?.actions?.length || 0;
                      const completed = h.completed_actions?.length || 0;
                      const pct = h.completion_pct || (totalActions > 0 ? Math.round((completed / totalActions) * 100) : 0);
                      return (
                        <div key={i} className="rounded-lg border border-border/50 p-3 bg-muted/30">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-xs font-medium text-foreground">
                                {SEASON_LABELS[h.plan?.season_phase] || 'Plan'}
                              </span>
                              <span className="text-[10px] text-muted-foreground ml-2">
                                {h.generated_at ? new Date(h.generated_at).toLocaleDateString() : ''}
                              </span>
                            </div>
                            <span className={`text-xs font-mono font-bold ${pct === 100 ? 'text-green-600' : 'text-muted-foreground'}`}>
                              {pct}%
                            </span>
                          </div>
                          <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
                            <div className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-primary/60'}`} style={{ width: `${pct}%` }} />
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {completed}/{totalActions} actions completed
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Activity Timeline */}
            {activity.length > 0 && (
              <div className="mt-6">
                <button
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowActivity(!showActivity)}
                  data-testid="plan-activity-toggle"
                >
                  <CalendarDots size={16} />
                  Activity Timeline ({activity.length})
                  {showActivity ? <CaretUp size={14} /> : <CaretDown size={14} />}
                </button>
                {showActivity && (
                  <div className="mt-3 space-y-1" data-testid="plan-activity-list">
                    {(() => {
                      // Group by date
                      const grouped = {};
                      activity.forEach(a => {
                        const date = new Date(a.completed_at).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
                        if (!grouped[date]) grouped[date] = [];
                        grouped[date].push(a);
                      });
                      return Object.entries(grouped).map(([date, items], gi) => (
                        <div key={gi} className="mb-3">
                          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">{date}</p>
                          {items.map((item, ii) => {
                            const Icon = ICON_MAP[item.category] || Flag;
                            const catColor = CATEGORY_COLORS[item.category] || 'text-primary';
                            return (
                              <div key={ii} className="flex items-center gap-2.5 py-1.5 pl-3 border-l-2 border-primary/20">
                                <div className={`shrink-0 ${catColor}`}><Icon size={14} weight="bold" /></div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-foreground truncate">{item.action_name}</p>
                                  {item.action_detail && <p className="text-[10px] text-muted-foreground truncate">{item.action_detail}</p>}
                                </div>
                                <span className="text-[10px] text-muted-foreground/60 shrink-0">
                                  {new Date(item.completed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()}
                    <p className="text-[9px] text-muted-foreground/50 italic pt-2">
                      History retained for {retentionDays === 'unlimited' ? 'unlimited' : `${retentionDays} days`}
                      {retentionDays !== 'unlimited' && ' — upgrade for full history'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Refresh CTA */}
            {!allComplete && (
              <div className="mt-8 text-center">
                <p className="text-xs text-muted-foreground mb-3">Plan updates based on your latest data</p>
                <Button variant="outline" size="sm" onClick={generatePlan} disabled={refreshing}>
                  <ArrowsClockwise size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                  Regenerate Plan
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
